import React, { Component } from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import CheckIcon from '@material-ui/icons/Check';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import axios from 'axios';
import moment from 'moment';
import { connect } from 'react-redux';
import customStyle from '../../../Assets/CSS/stylesheet_UHS';
import configurations from '../../../configurations';
import Loader from '../../loader';

// Carrier drives which stepper/quote this session belongs to: "Sharing Programs" => UHF, "Insurance Plans" => VERA.
const getStepperTypeFromCookies = (cookiesData) => {
    return cookiesData.actionType;
};

// applicationSubmitted is the definitive "done" signal — stepCode keeps moving past
// SUBMIT_APPLICATION (e.g. to COMPLETED) once it's submitted, so that check has to come
// first. Otherwise: not yet on Submit Application => still on an earlier step => pending.
// On Submit Application but not yet submitted => actionable (ENROLL).
const deriveEnrollmentStatus = (record) => {
    if (record.applicationSubmitted === true) return 'enrolled';
    if (record.stepCode !== 'SUBMIT_APPLICATION' && record.stepCode !== "REVIEW_CHOICES") return 'pending';
    return 'enroll';
};

// Landing screen for Cognito self-service members: lets them review their product choices
// and click ENROLL against each product before moving into Submit Application for
// Terms acceptance & e-sign. Standalone from ReviewChoice, which stays dedicated to the
// agent-driven multi-step wizard.
class EnrollmentSummary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loaderShow: true,
            enrollmentsTab: 'enrollments',
            reviewSummaryData: null,
            familyPackageData: null,
            enrollmentStatuses: null,
            effectiveDateFormatted: null,
            firstPaymentDateFormatted: null,
            effectiveDateShort: null,
            effectiveDate: null,
            draftDayFormatted: null,
            firstPaymentAmount: null,
            applicationFee: null,
            expandedSections: {},
            paymentType: null,
        };
    }

      componentDidMount() {
        this.fetchReviewChoicesData();
        this.fetchEnrollmentStatuses();
        this.fetchPaymentSummary();
        this.fetchFamilyPackage();
    }
    getSubId = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        return this.props.subId || cookiesData.subID || cookiesData.user_subId;
    }

    getCarrierLabel = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        return cookiesData.carrier || 'UHF';
    }

    toggleSection = (sectionIndex) => {
        this.setState((prevState) => ({
            expandedSections: {
                ...prevState.expandedSections,
                [sectionIndex]: prevState.expandedSections[sectionIndex] === true ? false : true,
            },
        }));
    }

    // Review & Approve only makes sense once every product's application has actually been
    // submitted — and it goes away again as soon as payment has succeeded, since there's
    // nothing left to review at that point.
    isAwaitingPayment = () => {
        const rows = this.state.enrollmentStatuses;
        if (!rows || rows.length === 0) return false;
        const allSubmitted = rows.every((row) => row.applicationSubmitted === true);
        if (!allSubmitted) return false;
        const paymentSucceeded = rows.some((row) => row.paymentStatus === 'SUCCESS' || row.paymentStatus === 'PENDING');
        return !paymentSucceeded;
    }

    // Launches the common-component-plugin's SetupPayment screen directly (loaded globally via
    // its plugin.iife.js script tag) rather than a native modal, so payment setup stays in sync
    // with the same screen used in Manage Enrollments.
    openPaymentSetup = () => {
        if (!window.loadCommonPluginScreen) {
            console.error('[CommonPlugin] loadCommonPluginScreen is not available — plugin script not loaded.');
            return;
        }
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        window.loadCommonPluginScreen(this.getSubId(), {
            type: 'SetupPayment',
            AgentID: cookiesData.brokerId,
            effectiveDate: this.state.effectiveDate,
            // The plugin screen is mounted straight to document.body, outside this component's
            // React tree, so componentDidMount never re-runs when it closes — refetch explicitly.
            actionClick: (data) => {
                console.log('payment setup done', data);
                this.fetchEnrollmentStatuses();
                this.fetchPaymentSummary();
            },
        });
    }

  

    fetchFamilyPackage = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        let subId = this.props.subId || cookiesData.subID || cookiesData.user_subId;
        let brokerId = cookiesData.brokerId;
        if (!subId || !brokerId) {
            this.setState({ familyPackageData: null });
            return;
        }
        axios.get(configurations.baseRootUrl + '/veraenroll/api/v1/quote/familyPackeage/' + subId + '/ALL/' + brokerId)
            .then((response) => {
                this.setState({ familyPackageData: response.data });
            })
            .catch((error) => {
                console.log(error);
                this.setState({ familyPackageData: null });
            });
    }

    // effectiveDate/draftDay drive the "on <date>" / "on the <nth> of every month" text appended
    // to the Program Summary header — they aren't part of review-choices, so this is a separate call.
    fetchPaymentSummary = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        let subId = this.props.subId || cookiesData.subID || cookiesData.user_subId;
        if (!subId) return;
        axios.get(configurations.baseRootUrl + '/veraenroll/api/v1/setuppayment/paymentSummary/' + subId)
            .then((response) => {
                let data = response.data && response.data.response;
                if (!data) return;
                // draftDay from the API is a day COUNT before the effective date, not the actual
                // calendar day of the month — e.g. effectiveDate 10/30 with draftDay 10 means the
                // 30th minus 10 days = the 20th. When that lands on the 29th/30th/31st (which not
                // every month has), show "last day" instead of a specific ordinal.
                let draftDayFormatted = null;
                if (data.draftDay != null && data.effectiveDate) {
                    // Subtracting via moment (not raw day-number arithmetic) correctly rolls back
                    // into the prior month when draftDay days takes it past the 1st. Uses
                    // moment.utc — the API sends these as UTC midnight, and plain moment() would
                    // reinterpret that in the browser's local time, shifting the day back by one
                    // for any timezone behind UTC.
                    const computedDraftDay = moment.utc(data.effectiveDate).subtract(data.draftDay, 'days').date();
                    draftDayFormatted = computedDraftDay >= 29
                        ? 'last day'
                        : moment.localeData().ordinal(computedDraftDay);
                }
                this.setState({
                    firstPaymentDateFormatted: data.firstPaymentDate ? moment.utc(data.firstPaymentDate).format('MMMM D, YYYY') : null,
                    effectiveDateFormatted: data.effectiveDate ? moment.utc(data.effectiveDate).format('MMMM D, YYYY') : null,
                    effectiveDateShort: data.effectiveDate ? moment.utc(data.effectiveDate).format('MM/DD/YYYY') : null,
                    effectiveDate: data.effectiveDate || null,
                    draftDayFormatted,
                    firstPaymentAmount: typeof data.firstPayment === 'number' ? data.firstPayment : null,
                    applicationFee: typeof data.applicationFee === 'number' ? data.applicationFee : null,
                    paymentType: data.paymentType || null
                });
            })
            .catch((error) => console.log(error));
    }

    fetchEnrollmentStatuses = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        let subId = this.props.subId || cookiesData.subID || cookiesData.user_subId;
        let brokerId = cookiesData.brokerId;
        if (!subId || !brokerId) {
            this.setState({ enrollmentStatuses: [] });
            return;
        }
        axios.get(configurations.baseRootUrl + '/veraenroll/api/v1/enrollment/stepper/product-type-wise/' + subId)
            .then((response) => {
                let records = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
                this.setState({
                    enrollmentStatuses: records.map((record) => ({
                        name: record.planName || "NA",
                        carrier: record.carrier || "NA",
                        productName: record.productName || "NA",
                        status: deriveEnrollmentStatus(record),
                        stepperType: record.stepperType,
                        paymentStatus: record.paymentStatus,
                        applicationSubmitted: record.applicationSubmitted === true,
                    })),
                });
            })
            .catch((error) => {
                console.log(error);
                this.setState({ enrollmentStatuses: [] });
            });
    }

    fetchReviewChoicesData = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        let subId = this.props.subId || cookiesData.subID || cookiesData.user_subId;
        let brokerId = cookiesData.brokerId;
        let stepperType = getStepperTypeFromCookies(cookiesData);
        let type = cookiesData.actionType;
        if (!subId || !brokerId) {
            this.setState({ loaderShow: false });
            return;
        }
        axios.get(configurations.baseRootUrl + '/veraenroll/api/v1/quote/review-choices/' + subId + '/' + "ALL" + '/' + brokerId)
            .then((response) => {
                this.setState({
                    reviewSummaryData: response.data.programFees,
                    loaderShow: false,
                });
            }).catch((error) => {
                console.log(error);
                this.setState({ loaderShow: false });
            });
    }

    renderEnrollments() {
        if (!this.state.enrollmentStatuses) {
            return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>Loading...</div>;
        }
        if (this.state.enrollmentStatuses.length === 0) {
            return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>No data available.</div>;
        }
        const rows = this.state.enrollmentStatuses;
        const isMobile = window.innerWidth <= 768;
        const programColumnWidth = isMobile ? '45%' : '30%';
        // All three status indicators share one fixed width/padding so their centers line up —
        // that shared center is where the dashed connector line (below) is drawn. Widened to fit
        // "E-Signature Complete", the longest label.
        const pillWidth = isMobile ? 160 : 220;
        const cellLeftPadding = 24;
        const pillBaseStyle = {
            display: 'inline-flex',
            // alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            width: pillWidth + 'px',
            padding: '3px 0px',
            borderRadius: '19px',
            fontWeight: '500',
            position: 'relative',
            zIndex: 1,
           
        };
        const connectorLineStyle = {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: (cellLeftPadding + pillWidth / 2) + 'px',
            borderLeft: '1px dashed #b3b3b3',
            zIndex: 0,
        };
        console.log('enrollmentStatuses', rows);    
        return (
            <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                <div>
                    <table style={{ ...customStyle.reviewChoicesTable, tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: programColumnWidth }} />
                            <col style={{ width: (100 - parseInt(programColumnWidth, 10)) + '%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th style={customStyle.TabTableHeaderCell}>Program / Plan</th>
                                <th style={{...customStyle.TabTableHeaderCell, padding: "17px 66px"}}>Enrollment</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    <td style={{ ...customStyle.reviewChoicesTableCell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><b>{row.name}</b></td>
                                    <td style={{ ...customStyle.reviewChoicesTableCell, position: 'relative' }}>
                                        <div style={connectorLineStyle} />
                                        {row.status === 'enrolled' &&
                                            <span style={{ ...pillBaseStyle, backgroundColor: '#2fb344', color: '#fff',  justifyContent: 'flex-start', }}>
                                                <CheckIcon style={{ fontSize: '21px', marginRight: '10px', marginLeft: '7px' }} /> E-Signature Complete
                                            </span>
                                        }
                                        {row.status === 'enroll' &&
                                            <Button
                                                variant="contained"
                                                style={{ ...pillBaseStyle, backgroundColor: '#1a2c40', color: '#fff', textTransform: 'none' }}
                                                onClick={() => this.props.onEnrollClick && this.props.onEnrollClick(row)}
                                            >
                                                ENROLL
                                            </Button>
                                        }
                                        {row.status === 'pending' &&
                                            <span style={{ ...pillBaseStyle, backgroundColor: '#e0e0e0', color: '#e05353', fontWeight: "400" }}>
                                                Application Pending
                                            </span>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderProgramSummary() {
        if (!this.state.reviewSummaryData) {
            return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>No data available.</div>;
        }
        const { body } = this.state.reviewSummaryData;
        // Section title + its total row are now merged into one collapsible header row (chevron
        // toggles the line items below), so there's no separate gray title bar anymore.
        const sectionHeaderCellStyle = { padding: '10px 24px', fontSize: '15px', color: '#19191d', fontWeight: 'bold', backgroundColor: '#f9f9f9', borderTop: '1px solid #98969c', borderBottom: '1px solid #98969c' };
        // Plain (non-sticky) variant of reviewChoicesTotalCell — that token pins to the bottom
        // of the scrollable body (for a <tfoot> that must stay visible while scrolling), which
        // makes it overlap short content when used as a normal last row like this one.
        const grandTotalCellStyle = { padding: '10px 24px', fontSize: '17px', fontWeight: 'bold', color: '#ffffff', backgroundColor: '#6f6d74' };
        // The API includes the grand total as its own body entry (no rows array — just a
        // title + todayAmount/regularAmount), alongside real sections whose rows can be empty
        // when nothing's selected for that carrier yet (status: "IN_PROGRESS"). Fall back to
        // summing sections' own total rows client-side if that entry isn't present.
        const sections = body.filter((item) => Array.isArray(item.rows));
        const grandTotalEntry = body.find((item) => !Array.isArray(item.rows));
        // "Application Fee - <plan name>" — the fee is for the primary plan, i.e. this
        // response's first section. section.title is sometimes a generic carrier-level label
        // ("Sharing Programs") rather than the specific plan, so prefer pulling the name out of
        // a row's own "<desc> for <plan>" text (e.g. "Monthly Share Contribution for Thrive
        // Healthshare 50") first, falling back to the title (minus any "UHS " prefix) only when
        // no row matches that shape.
        const getSectionPlanName = (section) => {
            if (!section) return null;
            for (const row of (section.rows || [])) {
                const match = row.text && row.text.match(/\bfor\s+(.+)$/i);
                if (match) return match[1];
            }
            return section.title ? section.title.replace(/^UHS\s+/i, '') : null;
        };
        const sectionPlanNames = sections.find((s) => s.title && s.title.toLowerCase().includes('sharing programs'));
        const applicationFeePlanName = getSectionPlanName(sectionPlanNames);
        const parseMoneyAmount = (str) => (!str || str === '-') ? 0 : (parseFloat(String(str).replace(/[^0-9.-]/g, '')) || 0);
        const formatMoneyAmount = (num) => '$' + num.toFixed(2);
        let grandRegularTotal = 0;
        if (!grandTotalEntry) {
            sections.forEach((section) => {
                if (!section.rows.length) return;
                const totalRow = section.rows[section.rows.length - 1];
                grandRegularTotal += parseMoneyAmount(totalRow.regularAmount);
            });
        }
        const recurringAmount = grandTotalEntry ? grandTotalEntry.regularAmount : formatMoneyAmount(grandRegularTotal);
        // draftDay isn't always populated — show a dash rather than leaving the cell blank when
        // there's no day-of-month to compute against.
        const recurringKeyDate = this.state.draftDayFormatted
            ? `${this.state.draftDayFormatted} of the month`
            : '-';
        return (
            <div>
                <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                    <div>
                        <table style={customStyle.reviewChoicesTable}>
                            <thead>
                                <tr>
                                    <th style={customStyle.FamilyTabTableHeaderCell}>Program / Plan</th>
                                    <th style={customStyle.FamilyTabTableHeaderCell}>Amount</th>
                                    <th style={customStyle.FamilyTabTableHeaderCell}>Key Dates</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.firstPaymentAmount != null &&
                                    <>
                                        <tr>
                                            <td style={grandTotalCellStyle}>Your First Payment</td>
                                            <td style={grandTotalCellStyle}>{formatMoneyAmount(this.state.firstPaymentAmount)}</td>
                                            <td style={grandTotalCellStyle}>{this.state.firstPaymentDateFormatted || '-'}</td>
                                        </tr>
                                        {this.state.applicationFee != null &&
                                            <tr>
                                                <td style={{ ...customStyle.reviewChoicesTableCell, fontWeight: 'bold' }}>{applicationFeePlanName ? `Application Fee - ${applicationFeePlanName}` : 'Application Fee'}</td>
                                                <td style={{ ...customStyle.reviewChoicesTableCell, fontWeight: 'bold' }}>{formatMoneyAmount(this.state.applicationFee)}</td>
                                                <td style={{ ...customStyle.reviewChoicesTableCell, fontWeight: 'bold' }}></td>
                                            </tr>
                                        }
                                    </>
                                }
                                <tr>
                                    <td style={grandTotalCellStyle}>Your Recurring Monthly Payment</td>
                                    <td style={grandTotalCellStyle}>{recurringAmount}</td>
                                    <td style={grandTotalCellStyle}>{recurringKeyDate}</td>
                                </tr>
                                {sections.map((section, sectionIndex) => {
                                    const rows = section.rows || [];
                                    // "One Time Application Fee" is already shown as its own
                                    // top-level "Application Fee - <plan>" row above — drop it
                                    // from the expanded line items so it isn't shown twice.
                                    const lineItems = rows.slice(0, -1).filter((row) => !/one[-\s]?time\s+application\s+fee/i.test(row.text || ''));
                                    const totalRow = rows.length ? rows[rows.length - 1] : null;
                                    const collapsible = lineItems.length > 0;
                                    const expanded = this.state.expandedSections[sectionIndex] === true;
                                    const headerCellStyle = collapsible ? { ...sectionHeaderCellStyle, cursor: 'pointer' } : sectionHeaderCellStyle;
                                    // section.title is sometimes a generic carrier-level label (e.g.
                                    // "Sharing Programs") rather than the specific plan — append the
                                    // plan name pulled from this section's own rows so the header
                                    // reads "Sharing Programs - Thrive Healthshare 50".
                                    const sectionPlanName = getSectionPlanName(section);
                                    const sectionLabel = sectionPlanName && sectionPlanName !== section.title
                                        ? `${section.title} - ${sectionPlanName}`
                                        : section.title;
                                    return (
                                        <React.Fragment key={sectionIndex}>
                                            <tr onClick={collapsible ? () => this.toggleSection(sectionIndex) : undefined}>
                                                <td style={headerCellStyle}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                                                            <ExpandMoreIcon style={{ visibility: collapsible ? 'visible' : 'hidden', fontSize: '20px', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s ease' }} />
                                                       
                                                        {sectionLabel}
                                                    </div>
                                                </td>
                                                <td style={headerCellStyle}>{totalRow ? totalRow.regularAmount : ''}</td>
                                                <td style={headerCellStyle}></td>
                                            </tr>
                                            {rows.length === 0 &&
                                                <tr>
                                                    <td colSpan={3} style={customStyle.reviewChoicesTableCell}>No selections yet.</td>
                                                </tr>
                                            }
                                            {collapsible && expanded && lineItems.map((row, rowIndex) => (
                                                <tr key={rowIndex}>
                                                    <td style={customStyle.reviewChoicesTableCell}>{row.text}</td>
                                                    <td style={customStyle.reviewChoicesTableCell}>{row.regularAmount}</td>
                                                    <td style={customStyle.reviewChoicesTableCell}></td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    renderFamilyDetails() {
        const familyPackageData = this.state.familyPackageData;
        // When no core plan has been selected yet for this carrier, the API returns a different
        // shape entirely — header is a plain column-name array (not the {memberId: name, text}
        // object this table expects), no sections, and a `message` explaining why. Object.keys()
        // on that array would produce bogus "member" columns, so treat it as empty instead.
        if (!familyPackageData || !familyPackageData.header || Array.isArray(familyPackageData.header)) {
            const emptyMessage = (familyPackageData && familyPackageData.message) || 'No data available.';
            return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>{emptyMessage}</div>;
        }
        const { header, sections } = familyPackageData;
        const safeSections = sections || [];
        // header/rows carry one dynamic key per family member (a numeric member id) alongside
        // the fixed text/familyTotal keys — Object.keys order matches insertion order from the API.
        const memberIds = Object.keys(header).filter((key) => key !== 'text');
        const memberHeaderCellStyle = { padding: '10px 24px', fontSize: '15px', fontWeight: 'bold', color: '#ffffff', backgroundColor: '#6f6d74', textAlign: 'center' };
        const memberHeaderTitleCellStyle = { ...memberHeaderCellStyle, textAlign: 'left' };
        const sectionCellStyle = { padding: '10px 24px', fontSize: '15px', color: '#19191d', backgroundColor: '#f9f9f9', borderTop: '1px solid #98969c', borderBottom: '1px solid #98969c' };
        const checkCellStyle = { ...sectionCellStyle, textAlign: 'center' };
        // Per-member cells are always "-" (costs aren't itemized by dollar amount here) — the
        // real enrolled/not-enrolled signal is whether a member's key is even PRESENT on a row,
        // not its value. Only the section's first row (its own core plan charge, e.g. "Monthly
        // Premium for ...") carries that signal reliably: later rows (family-wide fees, add-ons,
        // the isTotal rollup) can list OTHER members too since they just share those charges,
        // without those members being enrolled in this specific plan.
        const isMemberEnrolled = (section, memberId) => {
            const rows = section.rows || [];
            if (!rows.length) return false;
            return Object.prototype.hasOwnProperty.call(rows[0], memberId);
        };
        // Same "Sharing Programs - Thrive Healthshare 50" combo label as the PRODUCT SUMMARY
        // tab's section header — pulled from the matching review-choices section (a separate
        // API/state than familyPackageData) and keyed by title so each #f9f9f9 section row here
        // can show its own combo label where one applies.
        const summarySections = this.state.reviewSummaryData && Array.isArray(this.state.reviewSummaryData.body)
            ? this.state.reviewSummaryData.body.filter((item) => Array.isArray(item.rows))
            : [];
        const getSummaryPlanName = (section) => {
            if (!section) return null;
            for (const row of (section.rows || [])) {
                const match = row.text && row.text.match(/\bfor\s+(.+)$/i);
                if (match) return match[1];
            }
            return section.title ? section.title.replace(/^UHS\s+/i, '') : null;
        };
        const planNameByTitle = {};
        summarySections.forEach((s) => {
            const name = getSummaryPlanName(s);
            if (name && s.title && name !== s.title) planNameByTitle[s.title] = name;
        });
        return (
            <div>
                <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                    <div>
                        <table style={customStyle.reviewChoicesTable}>
                            <thead>
                                <tr>
                                    <th style={customStyle.FamilyTabTableHeaderCell}>{header.text}</th>
                                    <th colSpan={memberIds.length} style={{ ...customStyle.FamilyTabTableHeaderCell, textAlign: 'center' }}>Family Members Enrolled</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={memberHeaderTitleCellStyle}>Complete Health Package</td>
                                    {memberIds.map((memberId) => (
                                        <td key={memberId} style={memberHeaderCellStyle}>{header[memberId]}</td>
                                    ))}
                                </tr>
                                {safeSections.map((section, sectionIndex) => {
                                    const sectionLabel = planNameByTitle[section.title]
                                        ? `${section.title} - ${planNameByTitle[section.title]}`
                                        : section.title;
                                    return (
                                    <tr key={sectionIndex}>
                                        <td style={sectionCellStyle}><b>{sectionLabel}</b></td>
                                        {memberIds.map((memberId) => (
                                            <td key={memberId} style={checkCellStyle}>
                                                {isMemberEnrolled(section, memberId) &&
                                                    <CheckIcon style={{ color: '#2fb344', fontSize: '22px' }} />
                                                }
                                            </td>
                                        ))}
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {this.state.loaderShow ? <Loader></Loader> : ''}
                <Grid container style={{ fontFamily: 'Roboto, Arial, Helvetica, sans-serif', fontSize: '14px', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <div style={{ textAlign: 'justify', width: '95%' }}>
                                    <p>
                                      Your representative has already entered your information in the Enrollment Portal. You can review a summary of your product choices below and the family members enrolled. Once you are satisfied with the choices, you can click ENROLL against each Product. Review the terms,  indicate your acceptance of the same, and when you’re ready, click SUBMIT APPLICATION. Once all applications have been submitted, you can review and approve your payment terms. 
                                    </p>
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <div style={customStyle.reviewChoicesTabsStep} className="reviewChoicesTabsResponsive">
                                    <div
                                        style={this.state.enrollmentsTab === 'enrollments' ? customStyle.reviewChoicesTabActiveStep : customStyle.reviewChoicesTabInactiveStep}
                                        className="reviewChoicesTabResponsive"
                                        onClick={() => this.setState({ enrollmentsTab: 'enrollments' })}
                                    >ENROLLMENTS</div>
                                    <div
                                        style={this.state.enrollmentsTab === 'summary' ? customStyle.reviewChoicesTabActiveStep : customStyle.reviewChoicesTabInactiveStep}
                                        className="reviewChoicesTabResponsive"
                                        onClick={() => this.setState({ enrollmentsTab: 'summary' })}
                                    >PRODUCT SUMMARY</div>
                                    <div
                                        style={this.state.enrollmentsTab === 'family' ? customStyle.reviewChoicesTabActiveStep : customStyle.reviewChoicesTabInactiveStep}
                                        className="reviewChoicesTabResponsive"
                                        onClick={() => this.setState({ enrollmentsTab: 'family' })}
                                    >FAMILY DETAILS</div>
                                </div>
                                <div style={{ backgroundColor: '#fff' }}>
                                    {this.state.enrollmentsTab === 'enrollments'
                                        ? this.renderEnrollments()
                                        : this.state.enrollmentsTab === 'summary'
                                        ? this.renderProgramSummary()
                                        : this.renderFamilyDetails()}
                                    {this.state.effectiveDateShort &&
                                        <div style={{
                                            padding: '20px 0px',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '16px',
                                        }}>
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                backgroundColor: '#d9ecf9',
                                                color: '#3a7bbf',
                                                padding: '14px 24px',
                                                borderRadius: '10px',
                                                fontWeight: 'bold',
                                                fontSize: '15px',
                                            }}>
                                                <InfoOutlinedIcon style={{ fontSize: '22px' }} />
                                                Program Effective Date: {this.state.effectiveDateShort}
                                            </div>
                                        </div>
                                    }
                                </div>
                                {this.state.enrollmentsTab === 'enrollments' && this.isAwaitingPayment() &&
                                    <div style={{
                                        marginTop: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                        backgroundColor: '#f5f5f5',
                                        padding: '10px 24px',
                                        borderRadius: '10px',
                                        width: window.innerWidth <= 768 ? '100%' : '50%',
                                        boxSizing: 'border-box',
                                    }}>
                                        <span style={{ fontWeight: '400', fontSize: '17px', fontWeight: "bold" }}>
                                          {this.state.paymentType ? "Review and confirm your payment details and billing dates." : " Complete your payment details and confirm the billing dates."}
                                        </span>
                                        <Button
                                            variant="contained"
                                            style={{ backgroundColor: '#1a2c40', color: '#fff', textTransform: 'none', borderRadius: '20px', padding: '8px 24px', fontWeight: 'bold' }}
                                            onClick={this.openPaymentSetup}
                                        >
                                            {this.state.paymentType ? "REVIEW & CONFIRM" : "COMPLETE & CONFIRM"}
                                        </Button>
                                    </div>
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        subId: state.subId,
        email: state.email,
    };
}

export default connect(mapStateToProps)(EnrollmentSummary);
